import type { FC, ReactNode } from 'react'
import type { ProjectScreen } from '@/lib/projects'

import { useRef, useState } from 'react'
import { ArrowUpRight } from 'lucide-react'
import { cssVars } from '@/lib/utils'
import CropMarks from '@/components/generals/crop-marks'
import RegistrationMark from '@/components/generals/registration-mark'
import Marquee from '@/components/motion/marquee'
import TransitionLink from '@/components/motion/transition-link'
import { gsap, MOTION_QUERIES, ScrollTrigger, useGSAP } from '@/lib/motion'
import { clients } from '@/constants/content'

interface HeroProps {
    screens: ProjectScreen[]
}

interface TrackProps {
    cards: ProjectScreen[]
    unique: number
    colour?: boolean
}

// The skyline rhythm, repeated along the conveyor: each card's height as a
// share of the conveyor and how far it grows upwards while the page scrolls.
const SKYLINE = [
    { height: 64, lift: 40 },
    { height: 82, lift: 70 },
    { height: 56, lift: 22 },
    { height: 74, lift: 58 },
    { height: 68, lift: 46 },
    { height: 58, lift: 30 }
]

// Enough cards to fill wide screens before the loop repeats.
const MIN_CARDS = 8

const pad = (value: number): string => String(value).padStart(2, '0')

const fillTo = (screens: ProjectScreen[], count: number): ProjectScreen[] => {
    if (!screens.length) return []

    const filled: ProjectScreen[] = []

    while (filled.length < Math.max(count, screens.length)) {
        filled.push(screens[filled.length % screens.length])
    }

    return filled
}

// One copy of the conveyor. The grey copy holds the links; the colour copy sits
// inside the lens, offset so its cards line up with the grey ones beneath.
const Track: FC<TrackProps> = ({ cards, unique, colour = false }): ReactNode => {
    const set = cards.length / 2

    return (
        <>
            {cards.map((screen, index) => {
                const position = index % set
                const shape = SKYLINE[position % SKYLINE.length]
                const repeat = index >= unique
                const style = cssVars({ '--h': shape.height, '--lift': shape.lift, '--d': `${(position % 8) * 0.07}s` })

                if (colour) {
                    return (
                        <li key={`${screen.id}-${index}`} className='skyline-card intro-skyline relative shrink-0' style={style}>
                            <div data-shot='' className='absolute inset-0 overflow-hidden bg-ink'>
                                <img src={screen.screen_image ?? ''} alt='' decoding='async' className='size-full object-cover object-top' />
                            </div>
                        </li>
                    )
                }

                return (
                    <li key={`${screen.id}-${index}`} aria-hidden={repeat || undefined} className='skyline-card intro-skyline relative shrink-0' style={style}>
                        <TransitionLink
                            href={`/work/${screen.slug}`}
                            data-shot=''
                            data-cursor='View case'
                            tabIndex={repeat ? -1 : undefined}
                            aria-label={`${screen.name} case study`}
                            className='group absolute inset-0 block overflow-hidden bg-fore/5'
                        >
                            <img
                                src={screen.screen_image ?? ''}
                                alt={screen.screen_image_alt ?? ''}
                                loading={index < 7 ? 'eager' : 'lazy'}
                                decoding='async'
                                className='size-full object-cover object-top contrast-[1.05] grayscale transition-[filter,scale] duration-700 ease-expo group-hover:scale-[1.03] group-hover:grayscale-0'
                            />
                        </TransitionLink>
                    </li>
                )
            })}
        </>
    )
}

// The opening, built on the live site's idea: a skyline of real website
// screens drifting left on a lime band of clients drifting right. A measuring
// frame develops each screen into colour as it passes and names the project.
const Hero: FC<HeroProps> = ({ screens = [] }): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const conveyorRef = useRef<HTMLDivElement>(null)
    const mainTrackRef = useRef<HTMLUListElement>(null)
    const lensTrackRef = useRef<HTMLUListElement>(null)
    const lensRef = useRef<HTMLDivElement>(null)
    const headingRef = useRef<HTMLHeadingElement>(null)
    const fadeRef = useRef<HTMLDivElement>(null)
    const leadRef = useRef<HTMLDivElement>(null)
    const [active, setActive] = useState(0)

    const set = fillTo(screens, MIN_CARDS)
    const loop = [...set, ...set]

    useGSAP(() => {
        const section = sectionRef.current
        const conveyor = conveyorRef.current
        const main = mainTrackRef.current
        const lensTrack = lensTrackRef.current
        const lens = lensRef.current

        if (!section || !conveyor || !main || !lensTrack || !lens || !set.length) return

        const cards = Array.from(main.children) as HTMLElement[]
        const setMain = gsap.quickSetter(main, 'x', 'px')
        const setLens = gsap.quickSetter(lensTrack, 'x', 'px')
        let half = 0
        let lensCentre = 0
        let positions: Array<{ left: number, width: number }> = []
        let x = 0
        let current = -1

        const measure = (): void => {
            half = main.scrollWidth / 2
            lensCentre = lens.offsetLeft + lens.offsetWidth / 2
            positions = cards.slice(0, set.length).map(card => ({ left: card.offsetLeft, width: card.offsetWidth }))
        }

        // Which card sits under the lens, so the label can name it.
        const locate = (): void => {
            if (!half) return

            let point = (lensCentre - x) % half

            if (point < 0) point += half

            const found = positions.findIndex(({ left, width }) => point >= left - 6 && point < left + width + 6)

            if (found === -1) return

            const index = found % screens.length

            if (index !== current) {
                current = index
                setActive(index)
            }
        }

        const render = (): void => {
            setMain(x)
            setLens(x)
            locate()
        }

        const onResize = (): void => {
            measure()
            render()
        }

        measure()
        render()
        window.addEventListener('resize', onResize)

        const mm = gsap.matchMedia()

        mm.add(MOTION_QUERIES.motion, () => {
            let direction = 1
            let boost = 0
            let pace = 1

            const tick = (_time: number, deltaTime: number): void => {
                if (!half) return

                const speed = half / 60
                x -= direction * speed * (deltaTime / 1000) * (1 + boost) * pace
                boost *= 0.92
                x = ((x % half) - half) % half
                render()
            }

            const drift = ScrollTrigger.create({
                trigger: section,
                start: 'top bottom',
                end: 'bottom top',
                onToggle: (self) => {
                    if (self.isActive) gsap.ticker.add(tick)
                    else gsap.ticker.remove(tick)
                },
                onUpdate: (self) => {
                    direction = self.direction
                    boost = Math.min(Math.abs(self.getVelocity()) / 240, 8)
                }
            })

            if (drift.isActive) gsap.ticker.add(tick)

            // Hovering the screens slows the conveyor right down so a card can be picked.
            const slow = (): void => {
                pace = 0.15
            }

            const resume = (): void => {
                pace = 1
            }

            conveyor.addEventListener('pointerenter', slow)
            conveyor.addEventListener('pointerleave', resume)

            // Leaving the hero: the screens grow up out of the band while the
            // headline stretches on its width axis and lifts away.
            gsap.timeline({
                scrollTrigger: {
                    trigger: section,
                    start: 'top top',
                    end: 'bottom top',
                    scrub: 0.6
                }
            })
                .fromTo(conveyor, { '--rise': 0 }, { '--rise': 1, ease: 'none' }, 0)
                .fromTo(headingRef.current, { '--wdth': 108, yPercent: 0 }, { '--wdth': 125, yPercent: -28, ease: 'none' }, 0)
                .to([fadeRef.current, leadRef.current], { autoAlpha: 0, y: -24, ease: 'none', duration: 0.45 }, 0)

            return () => {
                gsap.ticker.remove(tick)
                drift.kill()
                conveyor.removeEventListener('pointerenter', slow)
                conveyor.removeEventListener('pointerleave', resume)
            }
        })

        return () => {
            window.removeEventListener('resize', onResize)
            mm.revert()
        }
    }, { scope: sectionRef, dependencies: [set.length, screens.length] })

    return (
        <section
            id='hero'
            ref={sectionRef}
            data-scene='paper'
            aria-label='Introduction'
            className='hero-conveyor relative flex min-h-svh flex-col overflow-hidden'
        >
            <div aria-hidden='true' className='bg-dots absolute inset-0' />

            <div className='relative z-10 px-5 pt-24 md:px-8 md:pt-28'>
                <div ref={fadeRef} className='type-label grid grid-cols-2 gap-4 text-quiet lg:grid-cols-12'>
                    <p className='intro-fade lg:col-span-3' style={cssVars({ '--d': '0.2s' })}>
                        Strategy, design and Webflow development
                    </p>

                    <p className='intro-fade text-right lg:col-span-3 lg:col-start-6 lg:text-left' style={cssVars({ '--d': '0.3s' })}>
                        London and Gdańsk, since 2012
                    </p>

                    <p className='intro-fade hidden lg:col-span-3 lg:col-start-10 lg:block lg:text-right' style={cssVars({ '--d': '0.4s' })}>
                        Award-winning design studio
                    </p>
                </div>

                <div className='relative mt-8 lg:mt-10 lg:pb-[max(0rem,calc(17rem-clamp(3.2rem,9.6vw,12.5rem)))]'>
                    <h1 ref={headingRef} className='type-display text-[clamp(3.2rem,9.6vw,12.5rem)]'>
                        <span className='block overflow-hidden pb-[0.04em]'>
                            <span className='intro-rise'>
                                Better websites
                            </span>
                        </span>

                        <span className='block overflow-hidden pb-[0.06em] lg:text-right'>
                            <span className='intro-rise' style={cssVars({ '--d': '0.08s' })}>
                                <em className='type-serif pr-[0.12em] text-pop'>start</em>here.
                            </span>
                        </span>
                    </h1>

                    <div ref={leadRef} className='mt-8 lg:absolute lg:top-[calc(clamp(3.2rem,9.6vw,12.5rem)*1.1)] lg:left-0 lg:mt-0 lg:max-w-[min(30vw,30rem)]'>
                        <p className='type-lead intro-fade' style={cssVars({ '--d': '0.45s' })}>
                            We plan, design and build websites for growing businesses: one senior team, from first workshop to launch.
                        </p>

                        <div className='intro-fade mt-6 flex flex-wrap gap-x-7 gap-y-3' style={cssVars({ '--d': '0.55s' })}>
                            <TransitionLink href='/work' className='group inline-flex items-center gap-2 font-medium'>
                                <span className='link-line'>
                                    View our work
                                </span>

                                <ArrowUpRight aria-hidden='true' className='size-4' />
                            </TransitionLink>

                            <TransitionLink href='/contact?service=free-homepage-preview' className='group inline-flex items-center gap-2 font-medium'>
                                <span className='link-line'>
                                    Request a homepage preview
                                </span>

                                <ArrowUpRight aria-hidden='true' className='size-4' />
                            </TransitionLink>
                        </div>
                    </div>
                </div>
            </div>

            {set.length > 0 && (
                <div className='relative mt-12 min-h-[340px] flex-1 lg:mt-10 lg:min-h-[300px]'>
                    <div className='absolute top-0 z-20 w-[var(--lens-w)] left-[var(--lens-x)]'>
                        <div className='type-label flex items-center justify-between text-quiet'>
                            <span>
                                In frame
                            </span>

                            <span className='tabular-nums text-fore'>
                                {pad(active + 1)} / {pad(screens.length)}
                            </span>
                        </div>

                        <div className='relative mt-1.5 h-6 overflow-hidden'>
                            <ul
                                className='transition-transform duration-700 ease-quart motion-reduce:transition-none'
                                style={{ transform: `translateY(calc(${active} * -1.5rem))` }}
                            >
                                {screens.map((screen, index) => (
                                    <li key={screen.id} aria-hidden={index !== active} className='flex h-6 items-center justify-between gap-3'>
                                        <TransitionLink
                                            href={`/work/${screen.slug}`}
                                            tabIndex={index === active ? undefined : -1}
                                            className='group flex min-w-0 items-center gap-1.5 text-sm font-semibold'
                                        >
                                            <span className='truncate' data-mdlfy-cms={`projects:${screen.id}:name`}>{screen.name}</span>
                                            <ArrowUpRight aria-hidden='true' className='size-3.5 shrink-0 transition-transform duration-500 ease-expo group-hover:-translate-y-0.5 group-hover:translate-x-0.5' />
                                        </TransitionLink>

                                        <span className='type-label truncate text-quiet' data-mdlfy-cms={`projects:${screen.id}:sector`}>{screen.sector}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    <div ref={conveyorRef} className='absolute inset-x-0 bottom-0 top-16'>
                        <ul
                            ref={mainTrackRef}
                            aria-label='Recent websites by Fit Design'
                            className='absolute bottom-0 left-0 top-0 flex w-max items-end gap-[var(--gap)] will-change-transform'
                        >
                            <Track cards={loop} unique={screens.length} />
                        </ul>

                        <div ref={lensRef} aria-hidden='true' className='pointer-events-none absolute bottom-0 top-0 z-10 w-[var(--lens-w)] overflow-hidden left-[var(--lens-x)]'>
                            <ul
                                ref={lensTrackRef}
                                className='absolute bottom-0 top-0 flex w-max items-end gap-[var(--gap)] will-change-transform left-[calc(var(--lens-x)*-1)]'
                            >
                                <Track cards={loop} unique={screens.length} colour />
                            </ul>
                        </div>

                        <div aria-hidden='true' className='pointer-events-none absolute bottom-0 top-0 z-10 w-[var(--lens-w)] text-fore left-[var(--lens-x)]'>
                            <CropMarks />
                            <span className='absolute inset-x-0 bottom-0 h-0.5 bg-pop' />
                        </div>
                    </div>
                </div>
            )}

            <div data-theme='volt' className='relative z-20 flex h-16 shrink-0 items-center bg-canvas text-fore md:h-[72px]'>
                <p className='type-label hidden h-full shrink-0 items-center border-r border-rule px-8 md:flex'>
                    150+ websites designed and built
                </p>

                <Marquee duration={52} reverse className='min-w-0 flex-1 font-heading text-[clamp(1.2rem,2vw,1.9rem)] font-[650] tracking-[-0.02em]'>
                    {clients.map(client => (
                        <span key={client} className='flex items-center whitespace-nowrap'>
                            {client}
                            <RegistrationMark className='mx-[0.8em] size-[0.5em] text-pop' />
                        </span>
                    ))}
                </Marquee>
            </div>
        </section>
    )
}

export default Hero
